import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { MemberUser } from '../../../../models/users/dto/member-user';
import { LoadingOptions } from '../../../../models/shared/loading-options';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ChangeInvestmentInstructionSubmissionTypeEnum } from '../../../../models/enum/shared/change-investment-instruction-submission-type.enum';
import { ChangeInvestmentInstructionsSubmissionFormViewModel } from './change-investment-instructions-submission-form-view-model';
import { AccountTypeIdEnum } from '../../../../models/enum/shared/account-type-id.enum';
import { map, shareReplay, tap } from 'rxjs/operators';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { InvestmentInstructionSubmissionFormService } from './investment-instruction-submission-form.service';
import { InvestmentInstructionFormRequest } from '../../../../models/users/requests/investment-instruction-form-request';
import { atLeastOneAccountSelectedValidator } from '../../validators/at-least-one-account-selected.validator';
import {
  ChangeInvestmentInstructionSubmissionAccountFormGroup,
  ChangeInvestmentInstructionSubmissionFormGroup
} from './form-groups/change-investment-instruction-submission-form-group';
import { GoverningJurisdictionNamePipe } from '../../pipes/governing-jurisdiction-name.pipe';
import { MemberAccount } from '../../../../models/transactions/member-account';
import { MemberAccountBalanceByFund } from '../../../../models/transactions/member-account-balance-by-fund';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-change-investment-instructions-submission-form',
  templateUrl: './change-investment-instructions-submission-form.component.html',
  styleUrls: ['./change-investment-instructions-submission-form.component.scss'],
  providers: [ChangeInvestmentInstructionsSubmissionFormViewModel, GoverningJurisdictionNamePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChangeInvestmentInstructionsSubmissionFormComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() user!: MemberUser;
  @Output() loadingOpts = new EventEmitter<LoadingOptions>();
  @Output() formSubmittedEvent = new EventEmitter<InvestmentInstructionFormRequest>();

  @ViewChild('form') formElement!: ElementRef<HTMLFormElement>;

  private viewModel = inject(ChangeInvestmentInstructionsSubmissionFormViewModel);
  private investmentInstructionService = inject(InvestmentInstructionSubmissionFormService);
  private governingJurisdictionNamePipe = inject(GoverningJurisdictionNamePipe);
  private destroyRef = inject(DestroyRef);

  public formSubmitted = false;

  public isLoading$ = this.viewModel.isLoading$;

  public formGroup: ChangeInvestmentInstructionSubmissionFormGroup = new FormGroup({
    submissionType: new FormControl<ChangeInvestmentInstructionSubmissionTypeEnum | null>(
      this.investmentInstructionService.selectedSubmissionType,
      Validators.required
    ),
    accountType: new FormControl<AccountTypeIdEnum | null>(
      this.investmentInstructionService.selectedAccountTypeId,
      Validators.required
    ),
    accounts: new FormArray<ChangeInvestmentInstructionSubmissionAccountFormGroup>([])
  });

  private _dcAccountsHidden = new BehaviorSubject(false);
  public dcAccountsHidden$ = this._dcAccountsHidden.asObservable();

  private _vbAccountsHidden = new BehaviorSubject(false);
  public vbAccountsHidden$ = this._vbAccountsHidden.asObservable();

  public dcAccountFormArray$ = combineLatest([
    this.viewModel.definedContributionAccounts$,
    this.viewModel.definedContributionAccountBalances$
  ]).pipe(
    map(([accounts, accountBalances]) => {
      const filteredAccounts = this.filterAccounts(accounts, accountBalances);
      this._dcAccountsHidden.next(filteredAccounts.length !== accounts.length);
      return filteredAccounts;
    }),
    map(accounts => {
      const formArray = new FormArray(
        accounts.map(
          account =>
            new FormGroup({
              id: new FormControl(account.id),
              accountTypeId: new FormControl(account.typeId),
              selected: new FormControl(this.isAccountPreselected(account)),
              name: new FormControl(account.employer),
              totalBalance: new FormControl(account.totalBalance)
            })
        )
      );

      formArray.updateValueAndValidity();
      return formArray;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public vbAccountFormArray$ = combineLatest([
    this.viewModel.variableBenefitAccounts$,
    this.viewModel.variableBenefitAccountBalances$
  ]).pipe(
    map(([accounts, accountBalances]) => {
      const filteredAccounts = this.filterAccounts(accounts, accountBalances);
      this._vbAccountsHidden.next(filteredAccounts.length !== accounts.length);
      return filteredAccounts;
    }),
    map(accounts => {
      const formArray = new FormArray(
        accounts.map(account => {
          const name = account.governingJurisdictionId
            ? this.governingJurisdictionNamePipe.transform(account.governingJurisdictionId)
            : $localize`Non-Locked-In`;

          return new FormGroup({
            id: new FormControl(account.id),
            accountTypeId: new FormControl(account.typeId),
            selected: new FormControl(this.isAccountPreselected(account)),
            name: new FormControl(name),
            totalBalance: new FormControl(account.totalBalance)
          });
        })
      );

      formArray.updateValueAndValidity();
      return formArray;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private listenToAccounts = combineLatest([this.dcAccountFormArray$, this.vbAccountFormArray$])
    .pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(([accounts, vbAccounts]) => {
        this.formGroup.setControl(
          'accounts',
          new FormArray([
            ...accounts.controls,
            ...vbAccounts.controls
          ]) as FormArray<ChangeInvestmentInstructionSubmissionAccountFormGroup>
        );
        this.formGroup.controls.accounts.setValidators(
          atLeastOneAccountSelectedValidator(() => this.formGroup.get('accountType')?.value)
        );
        this.formGroup.updateValueAndValidity();
      })
    )
    .subscribe();

  private isAccountPreselected(account: MemberAccount): boolean {
    const { selectedAccountId, selectedAccountTypeId } = this.investmentInstructionService;
    if (selectedAccountId !== null) {
      return account.id === selectedAccountId;
    }
    return account.typeId === selectedAccountTypeId;
  }

  private filterAccounts(accounts: MemberAccount[], accountBalances: MemberAccountBalanceByFund[]): MemberAccount[] {
    return accounts.filter(account => {
      const accountBalance = accountBalances.find(ab => ab.id === account.id);
      if (!accountBalance) return false;
      const cashFund = accountBalance.fundBalances.find(fb => fb.isCashFund());
      return accountBalance.totalBalance > 0 && cashFund?.totalBalance !== accountBalance.totalBalance;
    });
  }

  public submitForm(): void {
    this.formSubmitted = true;
    if (this.formGroup.valid) {
      const { submissionType, accountType, accounts } = this.formGroup.controls;
      const selectedAccountIds = accounts.controls
        .filter(control => control.value.selected)
        .map(control => control?.value?.id ?? -1);
      if (submissionType.value !== null && accountType.value !== null && selectedAccountIds?.length) {
        const request = new InvestmentInstructionFormRequest(
          submissionType.value,
          accountType.value,
          selectedAccountIds
        );
        this.formSubmittedEvent.emit(request);
      }
    }
  }

  private selectAllAccountsInGroup(accountTypeId: AccountTypeIdEnum): void {
    const formArray = this.formGroup.controls.accounts;
    formArray.controls.forEach(control => {
      if (control.value.accountTypeId === accountTypeId) {
        control.patchValue({ selected: true });
        control.enable();
      } else {
        control.patchValue({ selected: false });
        control.disable();
      }
    });
    this.formGroup.updateValueAndValidity();
  }

  public ngAfterViewInit(): void {
    this.formGroup.controls.accountType.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      if (value) {
        this.selectAllAccountsInGroup(value);
      }
    });

    this.formGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(c => {
      if (c?.accounts) {
        const selectedAccounts = c.accounts.filter(account => {
          return account.selected;
        });
        if (selectedAccounts?.length && !c?.accountType) {
          this.formGroup.controls.accountType.patchValue(selectedAccounts[0].accountTypeId ?? null, {
            emitEvent: false
          });
          this.formGroup.controls.accounts.updateValueAndValidity({ emitEvent: false });
        }
      }
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.user) {
      this.viewModel.setUser(this.user);
    }
  }

  public ngOnDestroy(): void {
    this.investmentInstructionService.setSelectedSubmissionType(null);
    this.investmentInstructionService.setSelectedAccountTypeId(null);
    this.investmentInstructionService.setSelectedAccountId(null);
  }

  protected readonly ChangeInvestmentInstructionSubmissionTypeEnum = ChangeInvestmentInstructionSubmissionTypeEnum;
  protected readonly AccountTypeIdEnum = AccountTypeIdEnum;
}
